/* eslint-disable react/require-default-props */
import * as React from 'react';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  ChevronDown,
  CircleHelp,
  LucideIcon,
  Menu as MenuIcon,
  MessageCircleMore,
  Search,
} from 'lucide-react';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { show } from '@intercom/messenger-js-sdk';
import { LoadedUser } from '../../interfaces';
import DropdownNavMenu from './DropdownNavMenu';
import useUser from '../../hooks/global/useUser';
import useSearchModal from '../../hooks/global/useSearchModal';
import './navigation.css';
import { AppContext } from '../../context/AppContext/context';
import Banners from './Banners';
import useBanners from '../../hooks/navigation/useBanners';
import { trimString } from '../../helpers/global';

const URL = {
  chat: '/chat',
  support: 'https://support.getporter.io',
};

const GAP = {
  xs: 0.5,
  sm: 1,
};

type Props = {
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loadedUser?: LoadedUser;
};

function ResponsiveAppBar({
  mobileDrawerOpen,
  setMobileDrawerOpen,
  loadedUser,
}: Props) {
  const { user } = useUser();
  const theme = useTheme();
  const matches = useMediaQuery('(min-width:900px)');
  const {
    state: { unreadConversations },
  } = React.useContext(AppContext);
  const [artistName, setArtistName] = React.useState<string>();
  const [artistInitials, setArtistInitials] = React.useState<string>();

  const { open } = useSearchModal();
  const { isBannerShown } = useBanners();

  const pages = React.useMemo(
    () => [
      {
        label: 'Search',
        onClick: open,
        icon: Search,
      },
      {
        label: 'Chat',
        url: URL.chat,
        icon: MessageCircleMore,
      },
      {
        label: 'Support',
        text: 'Support',
        icon: CircleHelp,
        onClick: show,
      },
    ],
    [open],
  );

  const unreadMessages = user
    ? Object.values(unreadConversations)?.reduce(
        (total, currentCount) => total + currentCount,
        0,
      ) || 0
    : 0;

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleDrawerChange = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  React.useEffect(() => {
    setArtistName(`${loadedUser?.firstName} ${loadedUser?.lastName}`);
    setArtistInitials(
      `${loadedUser?.firstName.charAt(0)}${loadedUser?.lastName.charAt(0)}`,
    );
  }, [loadedUser?.firstName, loadedUser?.lastName]);

  const classes = React.useMemo(() => {
    const list: string[] = [];
    list.push('primary-nav');
    if (isBannerShown) list.push('banner-above');
    return list;
  }, [isBannerShown]);

  const renderIcon = (Icon: LucideIcon) => (
    <Icon size={21} strokeWidth={1.75} color="#333333" />
  );

  return (
    <AppBar
      position="fixed"
      className={classes.join(' ')}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
      }}>
      <Banners />
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            height: '64px',
            gap: GAP,
          }}>
          <img src="/Logo.svg" alt="Porter logo" className="desktop-logo" />
          <div className="mobile-dropdown">
            <IconButton
              style={{ color: '#333333' }}
              aria-label="open drawer"
              onClick={handleDrawerChange}
              edge="start">
              <MenuIcon size={22} strokeWidth={1.75} />
            </IconButton>
            <img src="/Logo.svg" alt="Porter logo" />
          </div>
          <Stack
            flexGrow={1}
            flexDirection="row"
            justifyContent="end"
            gap={GAP}>
            {pages.map(page => (
              <Button
                key={page.label}
                to={page.url}
                component={page.url ? Link : Button}
                aria-label={page.label}
                onClick={() => {
                  setMobileDrawerOpen(false);
                  page.onClick?.();
                }}
                target={page.url?.startsWith('/') ? '_self' : '_blank'}
                sx={{
                  border: `1px solid ${theme.palette.outlineGrey.main}`,
                  borderRadius: '5px',
                  minWidth: 0,
                  my: 2,
                  display: 'flex',
                  gap: 1,
                }}>
                {page.url === URL.chat && !!unreadMessages ? (
                  <Badge color="primary" badgeContent={unreadMessages}>
                    {renderIcon(page.icon)}
                  </Badge>
                ) : (
                  renderIcon(page.icon)
                )}
                {page.text && (
                  <Typography
                    variant="h5"
                    color="text.primary"
                    display={{
                      xs: 'none',
                      sm: 'block',
                    }}>
                    {page.text}
                  </Typography>
                )}
              </Button>
            ))}
          </Stack>
          {loadedUser && (
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    sx={{
                      bgcolor: '#333333',
                      width: 30,
                      height: 30,
                      fontSize: 'body2.fontSize',
                      marginRight: matches ? 0 : 1,
                    }}>
                    {artistInitials || '  '}
                  </Avatar>
                  <div className="avatar-text hidden-mobile">
                    <Typography variant="h5">{artistName}</Typography>
                    <Typography variant="body2">
                      {trimString(loadedUser?.business?.name || '', 20)}
                    </Typography>
                  </div>
                  <ChevronDown size={20} strokeWidth={1.75} color="#666666" />
                </IconButton>
              </Tooltip>
              <DropdownNavMenu
                anchorElUser={anchorElUser}
                handleCloseDropdown={handleCloseUserMenu}
                setMobileDrawerOpen={setMobileDrawerOpen}
              />
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default ResponsiveAppBar;
